import { Link } from "react-router-dom";
import { Expand, ExternalLink, Fuel, Heart, MapPin } from "lucide-react";
import type { GasStation } from "@/types/database";
import { SafeImage } from "@/components/ui/SafeImage";
import { formatFuelPrice, googleMapsUrlForStation } from "@/lib/utils";
import { resolveStationLogo } from "@/lib/station-logos";

export function GasStationCard({ station }: { station: GasStation }) {
  const hasCoords = station.coordinatesLat != null && station.coordinatesLong != null;
  const logo = resolveStationLogo(station);

  return (
    <Link to={`/gas-stations/${station.id}`} className="group hover-raise block overflow-hidden rounded-3xl bg-canvas hover:bg-surface">
      <div className="relative flex h-24 items-center justify-center">
        {logo ? (
          <SafeImage
            src={logo}
            alt={station.name}
            width={56}
            height={56}
            className="object-contain transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <span className="text-lg font-bold text-[#d2d2d7]">{station.name.slice(0, 2)}</span>
        )}

        <div className="absolute right-3 top-3 flex gap-1.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 backdrop-blur-sm">
            <Expand className="h-3.5 w-3.5" />
          </span>
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 backdrop-blur-sm">
            <Heart className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>

      <div className="border-t border-border/60 px-3 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-bold">{station.name}</h3>
            <p className="mt-0.5 line-clamp-2 text-xs text-muted">{station.address}</p>
          </div>
          <div className="shrink-0 text-right">
            <p className="text-sm font-bold">{formatFuelPrice(station.fuelPrice_87)}</p>
            <p className="text-[10px] font-semibold text-muted">E-10 87</p>
          </div>
        </div>

        <div className="mt-2 flex items-center gap-2.5 text-[10px] font-semibold text-muted">
          {station.fuelPrice_90 ? (
            <span className="inline-flex items-center gap-1">
              <Fuel className="h-3 w-3" />
              90: {formatFuelPrice(station.fuelPrice_90)}
            </span>
          ) : null}
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {hasCoords ? "Mapped" : "No coords"}
          </span>
        </div>
      </div>
    </Link>
  );
}

export function GasStationMapPreview({ station }: { station: GasStation }) {
  const mapsUrl = googleMapsUrlForStation(station);

  if (station.coordinatesLat == null || station.coordinatesLong == null) {
    return (
      <div className="space-y-3">
        <div className="flex h-52 flex-col items-center justify-center rounded-2xl bg-[#f5f5f7] px-4 text-center text-sm text-muted">
          <MapPin className="mb-2 h-4 w-4" />
          Coordinates not available
        </div>
        {mapsUrl ? (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-full bg-canvas text-sm font-bold text-foreground transition-colors hover:bg-canvas-hover focus:outline-none focus:ring-2 focus:ring-accent"
          >
            <ExternalLink className="h-4 w-4" />
            Open in Google Maps
          </a>
        ) : null}
      </div>
    );
  }

  const { coordinatesLat: lat, coordinatesLong: lng } = station;
  const mapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01}%2C${lat - 0.01}%2C${lng + 0.01}%2C${lat + 0.01}&layer=mapnik&marker=${lat}%2C${lng}`;

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-2xl bg-[#f5f5f7]">
        <iframe title="Station map" src={mapUrl} className="h-52 w-full" loading="lazy" />
      </div>
      {mapsUrl ? (
        <a
          href={mapsUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-full bg-canvas text-sm font-bold text-foreground transition-colors hover:bg-canvas-hover focus:outline-none focus:ring-2 focus:ring-accent"
        >
          <ExternalLink className="h-4 w-4" />
          Open in Google Maps
        </a>
      ) : null}
    </div>
  );
}
